import { MinimizedSampleData, SearchClient } from './../../../web-api-client';
import { defaultCenter, maxZoom, minZoom, defaultZoom } from './config';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import mapboxgl, { Map } from 'mapbox-gl';
import { mapAccessToken } from './config';
import { GlobeService } from 'src/app/shared/services/globe.service';
import {
    getHexagonsFeatureCollection,
    zoomScale,
    buildLogCkmeansColorScale,
    colorForLogCkmeansValue,
    buildLogCkmeansFillColorExpression,
    buildGradientLegend,
    LogCkmeansColorScale,
    parse_measurements_minimized,
    SamplesHexagonData,
    HexagonData,
    MeasurementsHexagonData,
    d3smartFormat,
    formatStringWithUnitToHtml,
    partitionPolarFeatures,
} from './utility';
import { PolarHexagonCanvasOverlay } from './polar-hexagon-canvas-overlay';
import * as turf from '@turf/turf';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { DialogService } from '../../../shared/services/dialog.service';
import { DownloadService } from '../../../shared/services/download.service';
import { DownloadComponent } from '../../../shared/components/download/download.component';
import { finalize } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CommonSelectors } from 'src/app/store';
import { Nuclide } from 'src/app/store/models';
import { Subscription } from 'rxjs';
import { GeolocationService } from 'src/app/shared/services/geolocation.service';
import { LoadingService } from 'src/app/shared/services/loading.service';

const mapConfig = {
    layers: {
        hexbins: 'hexbins',
        hexbinsFill: 'hexbins-fill',
        hexbinsCentroid: 'hexbins-centroid',
        hexbinsLine: 'hexbins-line',
        hexbinsSingle: 'hexbins-single',
        areaIHOLayer: 'marisadmin.b8burbgp',
        areaIHOFillLayer: 'area-iho-fill-layer',
        areaFAOLayer: 'marisadmin.005nluox',
        areaFAOFillLayer: 'area-fao-fill-layer',
    },
    styles: {
        default: 'mapbox://styles/marisadmin/clwj91ua300iu01pceatr4tdc'
    },
    sources: {
        hexbins: 'hexbins',
        hexbinsCentroid: 'hexbins-centroid',
        areaIHO: 'area-iho-source',
        areaFAO: 'area-fao-source',
    },

    layersValues: () => [...Object.values(mapConfig.layers)],
};
const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    anchor: 'top-right', // Set the anchor to 'left' for the popup
});

interface PreparedHexagonLayers {
    nonPolar: any;
    polar: any;
    centroids: any;
}

@Component({
    selector: 'app-globe',
    templateUrl: './globe.component.html',
    styleUrls: ['./globe.component.scss'],
})
export class GlobeComponent implements OnInit, AfterViewInit, OnDestroy {
    @Output() onSeeAreaClick = new EventEmitter<any>();
    @Output() loadingComplete = new EventEmitter<any>();
    @Output() resetMapView = new EventEmitter<any>();
    @Output() measurementDataLoad = new EventEmitter<any>();

    currentStyle: string = mapConfig.styles.default;
    useStyle: boolean = false;
    hoveredHexId = null;
    sampleTypeId: number = 1;
    shapeTypeId: number = 1;
    filterValues: any;
    summaryStatisticsMode: string = 'uniqueSampleCount';
    data: any = [];
    globeData: HexagonData = null;
    map: Map;
    sampleCount: number = 0;
    measureCount: number = 0;
    nuclides: Nuclide[] = [];
    nuclideOptions: any = [];
    filterData: any;
    legendTitle: string = '';
    legendGradientBackground: string = '';
    legendTicks: { left: string; label: string }[] = [];
    draw: MapboxDraw;
    tooltip: mapboxgl.Popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
    });
    popup: mapboxgl.Popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: '500px',
    });
    activeComponent: typeof DownloadComponent;
    styleInitiated: boolean;
    drawModeActive: boolean = false;
    drawModeActiveTooltip: boolean = false;
    projection: string = 'globe';
    showBoundaries: boolean = false;
    zoom: any;
    isHexagonSelectionPossible: boolean = true;
    singleSelecteNuclideId: string = null;
    private subscriptions: Subscription[] = [];
    private browserZoomGuardTarget: HTMLElement | null = null;
    private readonly browserZoomGuardOptions: AddEventListenerOptions = {
        capture: true,
        passive: false,
    };
    private readonly browserZoomGuardRemovalOptions: EventListenerOptions = {
        capture: true,
    };
    latitude: number;
    longitude: number;
    private polarHexagonOverlay: PolarHexagonCanvasOverlay | null = null;
    private currentColorScale: LogCkmeansColorScale | null = null;
    private polarHoverActive = false;
    // Bug96383: Mapbox only re-measures its canvas/container on a native
    // `window.resize` event or an explicit `map.resize()` call - it has no
    // ResizeObserver of its own. A layout-only container resize (e.g. the page's
    // vertical scrollbar appearing/disappearing as content height changes) leaves
    // the map's internal transform width/canvas backing store stale relative to
    // the container's actual size, which desyncs rendering from hit-testing
    // (queryRenderedFeatures/hover/click) without any visible sign beyond that
    // mismatch. Observing the container directly and calling map.resize() makes
    // this self-correcting regardless of what causes the resize.
    private containerResizeObserver: ResizeObserver | null = null;
    private pendingContainerResizeFrame: number | null = null;
    // Tracks whether the map is actively being dragged/panned/rotated, so the
    // hexbins-fill/hexbins-single mousemove handlers below don't clobber the
    // 'grabbing' cursor if the drag path happens to cross a hexagon.
    private isMapDragging = false;

    constructor(
        private globeService: GlobeService,
        private searchClient: SearchClient,
        private downloadService: DownloadService,
        private dialogService: DialogService,
        private geolocationService: GeolocationService,
        private store: Store,
        private loadingService: LoadingService
    ) {}

    ngOnDestroy(): void {
        this.unregisterBrowserZoomGuards();
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.polarHexagonOverlay?.remove();
        this.containerResizeObserver?.disconnect();
        this.containerResizeObserver = null;
        if (this.pendingContainerResizeFrame !== null) {
            cancelAnimationFrame(this.pendingContainerResizeFrame);
            this.pendingContainerResizeFrame = null;
        }
    }

    ngOnInit(): void {
        this.initStoreSubscriptions();

        this.geolocationService
            .getCurrentPosition()
            .then((coords) => {
                this.latitude = coords.latitude;
                this.longitude = coords.longitude;

                let center = [this.longitude, this.latitude];

                this.initMapBox('globe', center, defaultZoom);
                this.initDialog();
                document
                    .querySelector('.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon')
                    .setAttribute(
                        'title',
                        'Define a free-form polygon to select and download data. To draw a polygon, click on the map to define each corner and double click to complete. To draw additional polygons, first click on the drawing mode icon each time. To deactivate the drawing mode, simply click on the trash icon”'
                    );
            })
            .catch((error) => {
                console.log('Error getting location:', error);

                this.initMapBox('globe', defaultCenter, defaultZoom);
                this.initDialog();
                document
                    .querySelector('.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon')
                    .setAttribute(
                        'title',
                        'Define a free-form polygon to select and download data. To draw a polygon, click on the map to define each corner and double click to complete. To draw additional polygons, first click on the drawing mode icon each time. To deactivate the drawing mode, simply click on the trash icon”'
                    );
            });
    }

    private initStoreSubscriptions() {
        this.subscriptions.push(
            this.store.select(CommonSelectors.selectNuclides).subscribe((nuclides) => {
                this.nuclides = nuclides;
            })
        );
    }

    private initDialog() {
        this.activeComponent = null;
        this.dialogService.activeComponent().subscribe((activeComponent) => {
            if (!activeComponent) this.activeComponent = null;
            else this.activeComponent = activeComponent;
        });
    }

    ngAfterViewInit(): void {
        this.globeService.getfilterValue().subscribe({
            next: (filterValue: any) => {
                this.legendTicks = [];
                this.filterValues = filterValue;
                if (!this.summaryStatisticsMode) {
                    this.summaryStatisticsMode = 'uniqueSampleCount';
                }
                this.singleSelecteNuclideId = filterValue.nuclideId;

                let skipLoadMeasurementsDataWhenSummaryStatisticsChanged = this.summaryStatisticsMode !== 'uniqueSampleCount' && this.summaryStatisticsMode !== this.filterValues.summaryStatisticsMode;

                this.summaryStatisticsMode = this.filterValues.summaryStatisticsMode;

                if (this.styleInitiated) {
                    this.loadData(skipLoadMeasurementsDataWhenSummaryStatisticsChanged);
                }
            },
            error: (err: any) => {
                console.error(err);
            },
            complete: () => {},
        });
    }

    private updateAreaLayers() {
        if (this.shapeTypeId == 1) {
            this.updateIHOLayersOnAreaChange();
        } else {
            this.updateFAOLayersOnAreaChange();
        }
    }

    onSummaryStatisticsChanged($event) {
        this.summaryStatisticsMode = $event;
        this.globeData = new MeasurementsHexagonData(this.data, this.summaryStatisticsMode);
        this.drawHexagons();
        if (this.isSample) {
            this.drawSampleLegend();
        } else {
            this.drawMeasurementLegend();
        }
    }

    private loadData(skipLoadMeasurementsDataWhenSummaryStatisticsChanged = false) {
        if (!this.filterValues) return;
        this.popup.remove();

        if (this.summaryStatisticsMode == 'uniqueSampleCount') {
            this.loadingService.setLoadingStatus(true);
            this.searchClient
                .getSampleDataMinimized(this.filterValues)
                .pipe(finalize(() => this.sendLoadingCompleted()))
                .subscribe({
                    next: (data) => {
                        this.setData(data);
                        this.initSampleAndMeasurementCount(
                            this.data.length,
                            this.data.reduce((a, b) => a + b.measureCount, 0)
                        );
                        this.createHexbinsLayers();

                        if (this.hexbinsSource) {
                            if (this.data.length == 0) {
                                this.resetHexbinsDataSource();
                            } else {
                                this.drawHexagons();
                                this.drawSampleLegend();
                            }
                        }
                    },
                    error: (err: any) => {
                        console.error(err);
                    },
                    complete: () => {},
                });
        } else {
            if (this.filterValues.areaId == '') {
                this.filterValues.nuclideId = '-999';
                this.filterValues.nuclideName = '-999';

                if (this.data.length == 0) {
                    this.resetHexbinsDataSource();
                }
            }

            if (skipLoadMeasurementsDataWhenSummaryStatisticsChanged) {
                return;
            }

            this.loadingService.setLoadingStatus(true);
            this.searchClient
                .getMeasurementDataMinimized(this.filterValues)
                .pipe(finalize(() => this.sendLoadingCompleted()))
                .subscribe({
                    next: (data) => {
                        let decompressedData = parse_measurements_minimized(data.result);
                        this.data = decompressedData;
                        this.globeData = new MeasurementsHexagonData(this.data, this.summaryStatisticsMode);

                        this.measurementDataLoad.emit(this.data);
                        const uniqueSampleIds = new Set(decompressedData.map((d) => d.sampleId));
                        this.initSampleAndMeasurementCount(uniqueSampleIds.size, this.data.length);

                        if (this.map) {
                            if (this.data.length == 0) {
                                this.resetHexbinsDataSource();
                            } else {
                                const featureCollection = this.getViewportHexagons();

                                if (this.hexbinsSource) {
                                    this.updateHexagonLayers(featureCollection);
                                } else {
                                    this.createHexbinsLayers(featureCollection);
                                }

                                this.applyColorScale();
                                this.drawMeasurementLegend();
                            }
                        }
                    },
                    error: (err: any) => {
                        console.error(err);
                    },
                    complete: () => {},
                });
        }
    }

    private drawMeasurementLegend() {
        const measurementFunctionLabel = this.getSummaryStatisticName(this.summaryStatisticsMode);
        this.drawLegend(`${this.nuclides.find((n) => n.id === Number(this.filterValues.nuclideId))?.lb} ${measurementFunctionLabel} (${this.getUnitLabel()})`);
    }

    private getUnitLabel() {
        const unitCorr = this.data.find((n) => Number(n.nuclideId) === Number(this.filterValues.nuclideId))?.unitCorr;
        if (unitCorr && unitCorr != 'NOT AVAILABLE') {
            return unitCorr;
        } else {
            return 'No Unit';
        }
    }

    // Reuses the LogCkmeansColorScale computed by the preceding applyColorScale()
    // call (drawHexagons()/the measurement-data load path always call
    // applyColorScale() before drawing a legend) so the legend can never drift
    // from the color actually painted on the hexagons.
    private drawLegend(title: string, formatingFunction: (value: number) => string | 0 = d3smartFormat) {
        this.legendTitle = formatStringWithUnitToHtml(title);

        if (!this.currentColorScale) {
            this.legendGradientBackground = '';
            this.legendTicks = [];
            return;
        }

        const legend = buildGradientLegend(this.currentColorScale, formatingFunction);
        this.legendGradientBackground = legend.gradientBackground;
        this.legendTicks = legend.ticks;
    }

    private drawSampleLegend() {
        // format by rounding without decimals
        this.drawLegend(`Sample count`);
    }

    private get hexbinsSource() {
        return this.map.getSource(mapConfig.sources.hexbins);
    }

    private get hexbinsCentroidSource() {
        return this.map.getSource(mapConfig.sources.hexbinsCentroid);
    }

    private get isSample() {
        return this.summaryStatisticsMode === 'uniqueSampleCount';
    }

    private drawHexagons() {
        const featureCollection = this.getViewportHexagons();
        this.updateHexagonLayers(featureCollection);
        this.applyColorScale();
    }

    // Single source of truth for hexagon coloring: computes one
    // LogCkmeansColorScale from the current data and applies it identically to
    // the Mapbox fill-color paint property (GPU-evaluated, non-polar hexagons)
    // and PolarHexagonCanvasOverlay (JS-evaluated, polar hexagons), so both
    // renderers -- and the legend, which reads the same this.currentColorScale
    // -- always agree on color for a given value.
    private applyColorScale() {
        this.currentColorScale = buildLogCkmeansColorScale(this.globeData.getColorData());
        this.map.setPaintProperty(mapConfig.layers.hexbinsFill, 'fill-color', buildLogCkmeansFillColorExpression(this.currentColorScale));
        this.polarHexagonOverlay?.setColorScale((value: number) => colorForLogCkmeansValue(value, this.currentColorScale));
        this.map.triggerRepaint();
    }

    private sendLoadingCompleted() {
        this.loadingComplete.emit();
        this.globeService.sendLoadingComplete({
            complete: 1,
        });
    }

    private getViewportHexagons() {
        const currentZoom = this.map.getZoom();
        return getHexagonsFeatureCollection(this.globeData, zoomScale(currentZoom));
    }

    private initSampleAndMeasurementCount(sampleCount = 0, measureCount = 0) {
        this.sampleCount = sampleCount;
        this.measureCount = measureCount;

        this.globeService.sendSampleCount(this.sampleCount);
        this.globeService.sendMeasureCount(this.measureCount);
    }

    private setData(data: MinimizedSampleData) {
        this.data = [];

        if (data.result) {
            data.result.split('|').forEach((item) => {
                const [id, measureCount, pos] = item.split(';');
                const [longitude, latitude] = pos.split(',').map(Number);
                this.data.push({
                    id: Number(id),
                    measureCount: Number(measureCount),
                    pos: pos,
                    latitude: latitude,
                    longitude: longitude,
                });
            });
        }

        this.globeData = new SamplesHexagonData(this.data);
    }

    private resetHexbinsDataSource() {
        this.updateHexagonLayers(this.emptyFeatureCollection());
    }

    private toCentroids(geojson) {
        const feature = {
            type: 'FeatureCollection',
            features: geojson
                ? geojson.features.map((d) => ({
                      ...d,
                      geometry: turf.centroid(d.geometry).geometry,
                  }))
                : [],
        };
        return feature;
    }

    private makePointPopupDOM(data, downloadHandler) {
        const rootEl = document.createElement('div');
        const formatDigits = d3smartFormat;

        rootEl.innerHTML = `<div class="mb-2 point-popup">
                <div class="popup-wrapper d-flex">

                    <div class="popup-block">
                        <div class="popup-row ${this.filterValues.sampleTypeId == 1 ? '' : 'd-none'}">
                            <div class="popup-title"><b>Sampling depth(s) (m)</b>: ${data.samplingDepth}</div>
                        </div>
                        <div class="popup-row">
                            <div class="popup-title"><b>Sampling year(s)</b>: ${data.samplingYear}</div>
                        </div>
                        <div class="popup-row">
                            <div class="popup-title"><b>Nuclide(s)</b>: ${data.nuclides?.join(', ')}</div>
                        </div>
                        <div class="popup-row ${this.filterValues.sampleTypeId === 2 ? '' : 'd-none'}" >
                            <div class="popup-title"><b>Biogroup(s)</b>: ${data.bioGroup}</div>
                        </div>
                        <div class="popup-row ${this.filterValues.sampleTypeId === 2 ? '' : 'd-none'}" >
                            <div class="popup-title"><b>N(taxons)</b>:${data.numberOfTaxons}</div>
                        </div>
                        <div class="popup-row">
                            <div class="popup-title"><b>N(datasets)</b>: ${data.numberOfDatasets}</div>
                        </div>
                        <div class="popup-row">
                            <div class="popup-title"><b>Datasets</b>: ${data.datasets?.join('; ')}</div>
                        </div>
                    </div>

                    <div class="popup-block">
                        <div class="popup-row">
                            <div class="popup-title"><b>N(samples)</b>: ${data.numberOfSamples}</div>
                        </div>
                        <div class="popup-row">
                            <div class="popup-title"><b>N(measurements – detected)</b>: ${data.numberOfSamplesWithEqualType}</div>
                        </div>
                        <div class="popup-row">
                            <div class="popup-title"><b>N(measurements – not detected)</b>: ${data.numberOfSamplesWithoutEqualType}</div>
                        </div>
                        <div class="${this.summaryStatisticsMode !== 'uniqueSampleCount' ? '' : 'd-none'}" >
                            <div class="popup-row">
                                <div class="popup-title"><b>AM ± SD</b>: ${data.AMSD}</div>
                            </div>
                            <div class="popup-row">
                                <div class="popup-title"><b>Median</b>: ${data.medianActivityString}</div>
                            </div>
                            ${!data.isC14 ? `
                            <div class="popup-row">
                                <div class="popup-title"><b>GM ± GSD</b>: ${data.GMGSD}</div>
                            </div>`: ''}
                            <div class="popup-row">
                                <div class="popup-title"><b>Maximum</b>: ${data.maxActivityString}</div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>`;

        const btnWrapEl = document.createElement('div');
        btnWrapEl.classList.add('d-flex', 'justify-content-center');
        btnWrapEl.innerHTML = `<button class="btn btn-primary">Download selected data</button>`;
        btnWrapEl.firstChild.addEventListener('click', downloadHandler);
        rootEl.appendChild(btnWrapEl);
        return rootEl;
    }

    private makeShapePopupDOM(data, downloadHandler) {
        const rootEl = document.createElement('div');
        rootEl.innerHTML = `<div class="popup-row">
                    <div class="popup-title"><b>Number of samples</b>: ${data.sampleCount}</div>
                </div>
                <div class="popup-row mb-2">
                    <div class="popup-title"><b>Number of measurements</b>: ${data.measureCount}</div>
                </div>`;
        const btnWrapEl = document.createElement('div');
        const disabled = data.sampleCount == 0 ? 'disabled' : '';
        btnWrapEl.innerHTML = `<button ` + disabled + ` class="btn btn-primary"> Download selected data</button>`;
        btnWrapEl.firstChild.addEventListener('click', downloadHandler);
        rootEl.appendChild(btnWrapEl);
        return rootEl;
    }

    private onUpdatePointPopup = (e) => {
        if (!this.isHexagonSelectionPossible) {
            return;
        }

        if (typeof e.features[0].geometry.coordinates[0] === 'number') {
            return;
        }

        const payload = JSON.parse(e.features[0].properties.value),
            statistics = payload.statistics,
            coordinates = payload.originalCoordinates[0],
            popupCoordinates = e.features[0].geometry.coordinates[0],
            params = {
                spatial: true,
                geoTypeId: 2,
                filterValues: this.filterValues,
                geoSpatialString: this.getGeometryString(coordinates, 'polygon'),
            };

        this.globeService.getSampleDetail(params).subscribe((v) => {
            this.popup
                .setLngLat(popupCoordinates[0])
                .setDOMContent(
                    this.makePointPopupDOM({ ...statistics, ...v }, () => {
                        this.dialogService.displayComponent(DownloadComponent);
                        this.downloadService.setFilterValue(params);
                    })
                )
                .addTo(this.map);
        });
    };

    private onUpdateShapePopup = (e) => {
        if (e.type == 'draw.delete') {
            this.drawModeActive = false;
            this.drawModeActiveTooltip = false;
            this.popup.remove();

            return;
        }

        if (!e.features?.length) return;

        if (typeof e.features[0].geometry.coordinates[0] === 'number') {
            return;
        }

        const coordinates = e.features[0].geometry.coordinates[0].slice(),
            params = {
                spatial: true,
                filterValues: this.filterValues,
                geoTypeId: 2,
                geoSpatialString: this.getGeometryString(coordinates, 'polygon'),
            };

        this.globeService.getGeographySampleDataSummary(params).subscribe((v) => {
            this.popup
                .setLngLat(coordinates[coordinates.length - 2])
                .setDOMContent(
                    this.makeShapePopupDOM({ sampleCount: v.sampleCount, measureCount: v.measureCount }, () => {
                        this.dialogService.displayComponent(DownloadComponent);
                        this.downloadService.setFilterValue(params);
                    })
                )
                .addTo(this.map);
        });
    };

    getGeometryString(positions: Array<Array<number>>, type: string) {
        if (type == 'polygon') {
            var str = positions.reduce((r, v) => r + `${v[0]} ${v[1]}, `, '');
            return 'POLYGON((' + str.substring(0, str.length - 2) + '))';
        }
        throw 'Not implemented';
    }

    closePolygonTooltip() {
        this.drawModeActiveTooltip = false;
    }

    private readonly preventBrowserPinchZoom = (event: WheelEvent) => {
        event.preventDefault();
    };

    private readonly preventSafariGestureZoom = (event: Event) => {
        event.preventDefault();
    };

    private registerBrowserZoomGuards(): void {
        this.unregisterBrowserZoomGuards();

        const target = this.map.getCanvasContainer();
        this.browserZoomGuardTarget = target;

        target.addEventListener('wheel', this.preventBrowserPinchZoom, this.browserZoomGuardOptions);
        target.addEventListener('gesturestart', this.preventSafariGestureZoom, this.browserZoomGuardOptions);
        target.addEventListener('gesturechange', this.preventSafariGestureZoom, this.browserZoomGuardOptions);
    }

    private unregisterBrowserZoomGuards(): void {
        if (!this.browserZoomGuardTarget) return;

        this.browserZoomGuardTarget.removeEventListener('wheel', this.preventBrowserPinchZoom, this.browserZoomGuardRemovalOptions);
        this.browserZoomGuardTarget.removeEventListener('gesturestart', this.preventSafariGestureZoom, this.browserZoomGuardRemovalOptions);
        this.browserZoomGuardTarget.removeEventListener('gesturechange', this.preventSafariGestureZoom, this.browserZoomGuardRemovalOptions);
        this.browserZoomGuardTarget = null;
    }

    // Bug96383: keeps the map's transform/canvas in sync with its actual container
    // size. Mapbox only re-measures on a native window resize or an explicit
    // map.resize() call; it has no observer of its own, so a layout-only resize of
    // the container (e.g. the page's scrollbar appearing/disappearing) otherwise
    // leaves the canvas backing store and hit-testing stale relative to what's
    // rendered. rAF-coalesced so rapid-fire ResizeObserver notifications within the
    // same frame only schedule a single map.resize().
    private registerContainerResizeObserver(): void {
        this.containerResizeObserver?.disconnect();

        this.containerResizeObserver = new ResizeObserver(() => {
            if (this.pendingContainerResizeFrame !== null) {
                return;
            }
            this.pendingContainerResizeFrame = requestAnimationFrame(() => {
                this.pendingContainerResizeFrame = null;
                this.map?.resize();
            });
        });
        this.containerResizeObserver.observe(this.map.getContainer());
    }

    initMapBox = (id: string | HTMLElement, center: number[], zoom: number): Promise<Map> => {
        mapboxgl.accessToken = mapAccessToken;

        this.map = new Map({
            container: id,
            center,
            zoom,
            style: `${this.currentStyle}`,
            projection: {
                name: this.projection,
            },
            maxZoom: maxZoom,
            minZoom: minZoom,
            bearing: 0,
        });

        this.map.scrollZoom.enable();
        this.registerBrowserZoomGuards();
        this.registerContainerResizeObserver();

        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: false },
        });
        this.map.addControl(this.draw);
        this.map.on('draw.create', this.onUpdateShapePopup);
        this.map.on('draw.delete', console.log('del'));
        this.map.on('draw.trash', this.onUpdateShapePopup);
        this.map.on('draw.update', this.onUpdateShapePopup);
        this.map.on('draw.selectionchange', this.onUpdateShapePopup);
        this.map.on('draw.modechange', (e) => {
            if (e.mode.startsWith('draw')) {
                this.drawModeActive = true;
                this.drawModeActiveTooltip = true;
            }
            if (e.mode == 'draw_polygon') this.isHexagonSelectionPossible = false;
            else setTimeout(() => (this.isHexagonSelectionPossible = true));
        });
        this.map.on('click', 'hexbins-fill', this.onUpdatePointPopup);
        this.map.on('touchend', 'hexbins-fill', this.onUpdatePointPopup);
        this.map.on('click', 'hexbins-single', this.onUpdatePointPopup);
        this.map.on('touchend', 'hexbins-single', this.onUpdatePointPopup);
        this.map.on('click', (e) => this.handlePolarHexagonClick(e));
        this.map.on('touchend', (e) => this.handlePolarHexagonClick(e));
        this.map.on('zoomstart', () => this.popup.remove());

        this.map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
        this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

        return new Promise((resolve) => {
            this.initMapHooks(popup, resolve);
            this.updateAreaLayers();
        });
    };

    private initMapHooks(popup: any, resolve: (value: any) => void = null) {
        this.map.on('load', async () => {
            if (resolve) {
                resolve(this.map);
            }
        });

        this.map.on('zoomend', () => {
            this.zoom = this.map.getZoom();
            this.drawHexagons();
            if (this.isSample) {
                this.drawSampleLegend();
            } else {
                this.drawMeasurementLegend();
            }
        });

        // When the user moves their mouse over the state-fill layer, we'll update the
        // feature state for the feature under the mouse.
        this.map.on('mousemove', 'hexbins-fill', (e) => {
            // Don't override the 'grabbing' cursor if the drag path happens to cross
            // a hexagon mid-drag.
            if (!this.isMapDragging) {
                this.map.getCanvas().style.cursor = 'pointer';
            }
            if (e.features.length > 0) {
                if (this.hoveredHexId !== null && this.hoveredHexId !== undefined) {
                    this.map.setFeatureState({ source: mapConfig.sources.hexbins, id: this.hoveredHexId }, { hover: false });
                }
                this.hoveredHexId = e.features[0].id;
                if (this.hoveredHexId !== null && this.hoveredHexId !== undefined) {
                    this.map.setFeatureState({ source: mapConfig.sources.hexbins, id: this.hoveredHexId }, { hover: true });
                }
            }

            // Copy coordinates array.
            const coordinates = turf.centroid(turf.centroid(e.features[0].geometry)).geometry.coordinates;

            const hoverDataLabel = this.summaryStatisticsMode == 'uniqueSampleCount' ? 'N(samples)' : this.getSummaryStatisticName(this.summaryStatisticsMode);

            var value = JSON.parse(e.features[0].properties.value).data;
            let description = '';
            if (value !== null) {
                if(value === 0) {
                    value = '<b>${hoverDataLabel}:</b> N/A';
                }
                if (this.summaryStatisticsMode === 'uniqueSampleCount') {
                    description = `<b>${hoverDataLabel}:</b> ${value}`;
                } else {
                    description = `<b>${hoverDataLabel}:</b> ${d3smartFormat(value)}`;
                }
            } else {
                description = `<b>${hoverDataLabel}:</b> 'N/A`;
            }

            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            // Populate the popup and set its coordinates
            // based on the feature found.
            this.tooltip.setLngLat(coordinates).setHTML(description).addTo(this.map);
        });

        // When the user moves their mouse over the state-fill layer, we'll update the
        // feature state for the feature under the mouse.
        this.map.on('mousemove', 'hexbins-single', (e) => {
            // See hexbins-fill mousemove above.
            if (!this.isMapDragging) {
                this.map.getCanvas().style.cursor = 'pointer';
            }
            if (e.features.length > 0) {
                if (this.hoveredHexId !== null && this.hoveredHexId !== undefined) {
                    this.map.setFeatureState(
                        {
                            source: 'hexbins-centroid',
                            id: this.hoveredHexId,
                        },
                        { hover: false }
                    );
                }
                this.hoveredHexId = e.features[0].id;
                if (this.hoveredHexId !== null && this.hoveredHexId !== undefined) {
                    this.map.setFeatureState({ source: 'hexbins-centroid', id: this.hoveredHexId }, { hover: true });
                }
            }

            // Copy coordinates array.
            const coordinates = turf.centroid(turf.centroid(e.features[0].geometry)).geometry.coordinates;
            const description = JSON.parse(e.features[0].properties.value).data;
            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            // Populate the popup and set its coordinates
            // based on the feature found.
            this.tooltip.setLngLat(coordinates).setHTML(description).addTo(this.map);
        });

        // When the mouse leaves the state-fill layer, update the feature state of the
        // previously hovered feature.
        this.map.on('mouseleave', 'hexbins-fill', () => {
            // Falling back to '' here would inherit Mapbox's own CSS
            // (.mapboxgl-canvas-container.mapboxgl-interactive { cursor: grab }), an
            // open-hand cursor shown over the entire map by default that makes it hard
            // to tell hexagons apart from empty space. 'default' shows a plain arrow
            // over empty globe area instead. Skipped while actively dragging so it
            // doesn't fight the dragstart/dragend handlers below.
            if (!this.isMapDragging) {
                this.map.getCanvas().style.cursor = 'default';
            }
            if (this.hoveredHexId !== null && this.hoveredHexId !== undefined) {
                this.map.setFeatureState({ source: mapConfig.sources.hexbins, id: this.hoveredHexId }, { hover: false });
            }
            this.hoveredHexId = null;
            this.tooltip.remove();
        });

        // dragstart/dragend are Mapbox's own composite events covering dragPan and
        // dragRotate, so this only changes the cursor style - it doesn't touch or
        // disable either interaction. On drag end, restore whichever cursor is
        // currently correct (pointer if still hovering a hexagon - non-polar or
        // polar - else default).
        this.map.on('dragstart', () => {
            this.isMapDragging = true;
            this.map.getCanvas().style.cursor = 'grabbing';
        });
        this.map.on('dragend', () => {
            this.isMapDragging = false;
            const hoveringHexagon = (this.hoveredHexId !== null && this.hoveredHexId !== undefined) || this.polarHoverActive;
            this.map.getCanvas().style.cursor = hoveringHexagon ? 'pointer' : 'default';
        });

        // Polar hexagons are drawn by PolarHexagonCanvasOverlay, a plain canvas
        // rather than a Mapbox layer, so they never receive layer-scoped mouse
        // events or participate in queryRenderedFeatures. Hit-test them manually,
        // deferring to the hexbins-fill/hexbins-single handlers above whenever the
        // cursor is over one of those (mutually exclusive by latitude threshold).
        this.map.on('mousemove', (e) => this.handlePolarHexagonHover(e));

        this.map.on('style.load', (e) => {
            this.initMapControlsAndLayers();

            //Do not show area layers by default
            this.hideIHOAreaLayers();
            this.hideFAOAreaLayers();

            if (!this.styleInitiated) {
                this.loadData();
                this.styleInitiated = true;
            } else if (this.globeData && !this.globeData.empty) {
                this.createHexbinsLayers();
                this.drawHexagons();
            }
        });

        const clickHandler = (e) => {
            const point = e.point;
            const features = this.map.queryRenderedFeatures(point);

            // hexbins is top priority click handler
            const hexbinsFillFeatures = features.filter((feature) => feature.layer.id === mapConfig.layers.hexbinsFill);

            if (hexbinsFillFeatures.length) {
                return;
            }

            // polar hexagons (drawn via PolarHexagonCanvasOverlay, not a Mapbox
            // layer) share the same top priority as hexbins-fill.
            if (this.polarHexagonOverlay?.featureAtPoint(point)) {
                return;
            }

            // area-layer is second priority click handler
            const areaLayerFeatures = features.filter((feature) => feature.layer.id === mapConfig.layers.areaIHOFillLayer || feature.layer.id === mapConfig.layers.areaFAOFillLayer);

            if (areaLayerFeatures.length && !this.drawModeActive && this.showBoundaries) {
                this.onSeeAreaClick.emit(areaLayerFeatures[0].properties.MRGID || areaLayerFeatures[0].properties.F_AREA);
                return;
            }
        };

        this.map.on('click', (e) => clickHandler(e));
        this.map.on('click', (e) => clickHandler(e));
    }

    private initMapControlsAndLayers() {
        this.addIHOAreaLayers();
        this.addFAOAreaLayers();
        this.addButtons();
    }

    hideIHOAreaLayers() {
        this.map.setLayoutProperty(mapConfig.layers.areaIHOLayer, 'visibility', 'none');
        this.map.setLayoutProperty(mapConfig.layers.areaIHOFillLayer, 'visibility', 'none');
    }

    showIHOAreaLayers() {
        this.map.setLayoutProperty(mapConfig.layers.areaIHOLayer, 'visibility', 'visible');
        this.map.setLayoutProperty(mapConfig.layers.areaIHOFillLayer, 'visibility', 'visible');
    }

    hideFAOAreaLayers() {
        this.map.setLayoutProperty(mapConfig.layers.areaFAOLayer, 'visibility', 'none');
        this.map.setLayoutProperty(mapConfig.layers.areaFAOFillLayer, 'visibility', 'none');
    }

    showFAOAreaLayers() {
        this.map.setLayoutProperty(mapConfig.layers.areaFAOLayer, 'visibility', 'visible');
        this.map.setLayoutProperty(mapConfig.layers.areaFAOFillLayer, 'visibility', 'visible');
    }

    onResetMapView() {
        const map = this.map;
        map.setZoom(defaultZoom);
        map.setBearing(0);
        map.setPitch(0);

        this.resetMapView.emit();

        // this.geolocationService
        //     .getCurrentPosition()
        //     .then((coords) => {
        //         this.latitude = coords.latitude;
        //         this.longitude = coords.longitude;

        //         let center = [this.longitude, this.latitude];

        //         map.setCenter(center);
        //     })
        //     .catch((error) => {
        //         map.flyTo({
        //             center: defaultCenter,
        //         });
        //     });
    }

    private createHexbinsLayers(hexagonsOverride: any = null, preparedLayers?: PreparedHexagonLayers) {
        if (this.hexbinsSource) return;

        const hexagons = hexagonsOverride || this.getViewportHexagons();
        const { nonPolar, polar, centroids } = preparedLayers || this.prepareHexagonLayers(hexagons);

        this.map.addSource(mapConfig.layers.hexbins, {
            type: 'geojson',
            data: nonPolar,
            generateId: true,
        });

        this.map.addSource(mapConfig.layers.hexbinsCentroid, {
            type: 'geojson',
            data: centroids,
        });

        this.map.addLayer({
            id: mapConfig.layers.hexbinsFill,
            type: 'fill',
            source: mapConfig.sources.hexbins,
            layout: {},
            paint: {
                // Placeholder shown only until the first applyColorScale() call
                // sets the real interpolate expression once data is loaded.
                'fill-color': '#d9d9d9',
            },
        });

        this.map.addLayer({
            id: mapConfig.layers.hexbinsLine,
            type: 'line',
            source: mapConfig.sources.hexbins,
            layout: {},
            paint: {
                'line-color': ['case', ['boolean', ['feature-state', 'hover'], false], 'black', '#bbb'],
                'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 4, 0.5],
            },
        });

        this.map.addLayer({
            id: mapConfig.layers.hexbinsSingle,
            type: 'circle',
            source: mapConfig.sources.hexbinsCentroid,
            layout: {},
            paint: {
                'circle-color': 'white',
                'circle-radius': 2,
            },
            filter: ['==', ['get', 'itemLength'], 1],
        });

        this.updatePolarHexagonLayer(polar);
    }

    private updateHexagonLayers(featureCollection: any, preparedLayers?: PreparedHexagonLayers) {
        const { nonPolar, polar, centroids } = preparedLayers || this.prepareHexagonLayers(featureCollection);
        const hexbins = this.hexbinsSource;
        if (hexbins) hexbins.setData(nonPolar);

        const hexbinsCentroid = this.hexbinsCentroidSource;
        if (hexbinsCentroid) hexbinsCentroid.setData(centroids);

        this.updatePolarHexagonLayer(polar);
    }

    private prepareHexagonLayers(featureCollection: any): PreparedHexagonLayers {
        if (!this.isGlobeProjection()) {
            return { nonPolar: featureCollection, polar: this.emptyFeatureCollection(), centroids: this.toCentroids(featureCollection) };
        }

        const { nonPolar, polar } = partitionPolarFeatures(featureCollection);
        return { nonPolar, polar, centroids: this.toCentroids(nonPolar) };
    }

    private updatePolarHexagonLayer(polarFeatureCollection: any) {
        if (!this.map) {
            return;
        }

        if (!this.isGlobeProjection()) {
            this.polarHexagonOverlay?.remove();
            this.polarHexagonOverlay = null;
            this.map.getCanvas().style.cursor = '';
            this.tooltip.remove();
            this.polarHoverActive = false;
            return;
        }

        if (!this.polarHexagonOverlay) {
            this.polarHexagonOverlay = new PolarHexagonCanvasOverlay(this.map);
            if (this.currentColorScale) {
                this.polarHexagonOverlay.setColorScale((value: number) => colorForLogCkmeansValue(value, this.currentColorScale));
            }
        }

        this.polarHexagonOverlay.setData(polarFeatureCollection);
    }

    private isGlobeProjection(): boolean {
        return this.map?.getProjection()?.name === 'globe';
    }

    private handlePolarHexagonHover(e: mapboxgl.MapMouseEvent) {
        if (!this.polarHexagonOverlay) {
            return;
        }

        // hexbins-fill/hexbins-single already have their own mousemove/mouseleave
        // handlers (feature-state hover, tooltip, cursor); defer to them whenever
        // the cursor is over a non-polar cell so the two mechanisms don't fight.
        const nonPolarHit = this.map.queryRenderedFeatures(e.point, {
            layers: [mapConfig.layers.hexbinsFill, mapConfig.layers.hexbinsSingle],
        });
        if (nonPolarHit.length > 0) {
            return;
        }

        const feature = this.polarHexagonOverlay.featureAtPoint(e.point);
        if (!feature) {
            if (this.polarHoverActive) {
                if (!this.isMapDragging) {
                    this.map.getCanvas().style.cursor = 'default';
                }
                this.tooltip.remove();
                this.polarHoverActive = false;
            }
            return;
        }

        this.polarHoverActive = true;
        // Don't override the 'grabbing' cursor if the drag path happens to cross a
        // polar hexagon mid-drag (mirrors the hexbins-fill/hexbins-single handling).
        if (!this.isMapDragging) {
            this.map.getCanvas().style.cursor = 'pointer';
        }

        const hoverDataLabel = this.summaryStatisticsMode == 'uniqueSampleCount' ? 'N(samples)' : this.getSummaryStatisticName(this.summaryStatisticsMode);
        const value = feature.properties?.value?.data;
        let description: string;
        if (value === null || value === undefined || value === 0) {
            description = `<b>${hoverDataLabel}:</b> N/A`;
        } else if (this.summaryStatisticsMode === 'uniqueSampleCount') {
            description = `<b>${hoverDataLabel}:</b> ${value}`;
        } else {
            description = `<b>${hoverDataLabel}:</b> ${d3smartFormat(value)}`;
        }

        // Anchor on the feature's own centroid rather than `e.lngLat`: Mapbox's
        // screen-to-lngLat inverse projection becomes numerically unstable very
        // close to the pole (longitude is near-degenerate there), which was
        // causing the tooltip to reappear far from the cursor, near the canvas
        // edge, once popup re-projected that inaccurate lngLat back to screen.
        const anchor = turf.centroid(feature.geometry).geometry.coordinates;
        this.tooltip.setLngLat(anchor).setHTML(description).addTo(this.map);
    }

    // Mirrors onUpdatePointPopup, which does the same thing for hexbins-fill.
    // properties.value here is already a plain object (never serialized through
    // a Mapbox GeoJSON source), so unlike onUpdatePointPopup this skips JSON.parse.
    private handlePolarHexagonClick(e: mapboxgl.MapMouseEvent) {
        if (!this.polarHexagonOverlay || !this.isHexagonSelectionPossible) {
            return;
        }

        const feature = this.polarHexagonOverlay.featureAtPoint(e.point);
        if (!feature) {
            return;
        }

        const payload = feature.properties?.value;
        const originalCoordinates = payload?.originalCoordinates?.[0];
        const popupCoordinates = feature.geometry?.coordinates?.[0];
        if (!originalCoordinates || !popupCoordinates?.length) {
            return;
        }

        const params = {
            spatial: true,
            geoTypeId: 2,
            filterValues: this.filterValues,
            geoSpatialString: this.getGeometryString(originalCoordinates, 'polygon'),
        };

        this.globeService.getSampleDetail(params).subscribe((v) => {
            this.popup
                .setLngLat(popupCoordinates[0])
                .setDOMContent(
                    this.makePointPopupDOM({ ...payload.statistics, ...v }, () => {
                        this.dialogService.displayComponent(DownloadComponent);
                        this.downloadService.setFilterValue(params);
                    })
                )
                .addTo(this.map);
        });
    }

    private emptyFeatureCollection() {
        return { type: 'FeatureCollection', features: [] };
    }

    private addIHOAreaLayers() {
        this.map.addSource(mapConfig.sources.areaIHO, {
            type: 'vector',
            url: 'mapbox://trivectorx.6eec4e16',
        });

        this.map.addLayer({
            id: mapConfig.layers.areaIHOLayer,
            type: 'line',
            source: mapConfig.sources.areaIHO,
            'source-layer': 'World_Seas_IHO_v3',
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
                visibility: this.shapeTypeId == 1 ? 'visible' : 'none',
            },
        });

        this.map.addLayer({
            id: mapConfig.layers.areaIHOFillLayer,
            type: 'fill',
            source: mapConfig.sources.areaIHO,
            'source-layer': 'World_Seas_IHO_v3',
            layout: {
                visibility: this.shapeTypeId == 1 ? 'visible' : 'none',
            },
        });

        this.setDefaultIHOLayerProperties();
    }

    private addFAOAreaLayers() {
        this.map.addSource(mapConfig.sources.areaFAO, {
            type: 'vector',
            url: 'mapbox://trivectorx.dsyk8iy9',
        });

        this.map.addLayer({
            id: mapConfig.layers.areaFAOLayer,
            type: 'line',
            source: mapConfig.sources.areaFAO,
            'source-layer': 'fao-simplified-bhxrme',
            paint: {
                'line-color': '#3561ce',
                'line-opacity': 0.7,
                'line-width': 2,
            },
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
                visibility: this.shapeTypeId == 2 ? 'visible' : 'none',
            },
        });

        this.map.addLayer({
            id: mapConfig.layers.areaFAOFillLayer,
            type: 'fill',
            source: mapConfig.sources.areaFAO,
            'source-layer': 'fao-simplified-bhxrme',
            paint: {
                'fill-color': ['case', ['in', 'F_AREA', ['literal', ['37']]], '#3561ce', 'transparent'],
                'fill-opacity': 0.25,
                'fill-outline-color': '#3561ce',
            },
            layout: {
                visibility: this.shapeTypeId == 2 ? 'visible' : 'none',
            },
        });

        this.setDefaultFAOLayerProperties();
    }

    private updateIHOLayersOnAreaChange() {
        const lineLayer = this.map.getLayer(mapConfig.layers.areaIHOLayer);
        const fillLayer = this.map.getLayer(mapConfig.layers.areaIHOFillLayer);

        if (!lineLayer || !fillLayer) {
            return;
        }

        var areas = this.filterValues?.areaId?.split(',').map((x) => Number(x)) || [];
        if (areas.length > 0) {
            var currentLinePaint = lineLayer.paint;
            const newLinePaint = {
                ...currentLinePaint,
                'line-color': ['case', ['in', ['get', 'MRGID'], ['literal', areas]], '#3561ce', '#3561ce'],
                'line-opacity': ['case', ['in', ['get', 'MRGID'], ['literal', areas]], 1.5, 0.1],
                'line-width': ['case', ['in', ['get', 'MRGID'], ['literal', areas]], 1.5, 1],
            };
            lineLayer.paint = newLinePaint;
            this.map.setPaintProperty(mapConfig.layers.areaIHOLayer, 'line-color', newLinePaint['line-color']);
            this.map.setPaintProperty(mapConfig.layers.areaIHOLayer, 'line-opacity', newLinePaint['line-opacity']);
            this.map.setPaintProperty(mapConfig.layers.areaIHOLayer, 'line-width', newLinePaint['line-width']);

            var currentFillPaint = fillLayer.paint;
            const newFillPaint = {
                ...currentFillPaint,
                'fill-color': ['case', ['in', ['get', 'MRGID'], ['literal', areas]], '#3561ce', 'transparent'],
                'fill-opacity': ['case', ['in', ['get', 'MRGID'], ['literal', areas]], 0.25, 0],
            };
            fillLayer.paint = newFillPaint;
            this.map.setPaintProperty(mapConfig.layers.areaIHOFillLayer, 'fill-color', newFillPaint['fill-color']);
            this.map.setPaintProperty(mapConfig.layers.areaIHOFillLayer, 'fill-opacity', newFillPaint['fill-opacity']);

            const area = this.findAreaByMRGID(areas[0], 'World_Seas_IHO_v3');

            //center map to area
            if (area) {
                const center = turf.centroid(area.geometry).geometry.coordinates;
                this.map.flyTo({ center });
            }
        } else {
            this.setDefaultIHOLayerProperties();
        }
    }

    private updateFAOLayersOnAreaChange() {
        const lineLayer = this.map.getLayer(mapConfig.layers.areaFAOLayer);
        const fillLayer = this.map.getLayer(mapConfig.layers.areaFAOFillLayer);

        if (!lineLayer || !fillLayer) {
            return;
        }

        var areas = this.filterValues?.areaId?.split(',').map((x) => x) || [];
        if (areas.length) {
            areas.push(this.sanitizeFAOAreaId(areas[0]));
        }
        var currentLinePaint = lineLayer.paint;
        let newLinePaint = {};
        if (areas.length > 0) {
            newLinePaint = {
                ...currentLinePaint,
                'line-color': ['case', ['in', ['get', 'F_AREA'], ['literal', areas]], '#3561ce', '#3561ce'],
                'line-opacity': ['case', ['in', ['get', 'F_AREA'], ['literal', areas]], 0.5, 0.1],
                'line-width': ['case', ['in', ['get', 'F_AREA'], ['literal', areas]], 1.5, 1],
            };

            lineLayer.paint = newLinePaint;
            this.map.setPaintProperty(mapConfig.layers.areaFAOLayer, 'line-color', newLinePaint['line-color']);
            this.map.setPaintProperty(mapConfig.layers.areaFAOLayer, 'line-opacity', newLinePaint['line-opacity']);
            this.map.setPaintProperty(mapConfig.layers.areaFAOLayer, 'line-width', newLinePaint['line-width']);

            var currentFillPaint = fillLayer.paint;
            const newFillPaint = {
                ...currentFillPaint,
                'fill-color': ['case', ['in', ['get', 'F_AREA'], ['literal', areas]], '#3561ce', 'transparent'],
                'fill-opacity': ['case', ['in', ['get', 'F_AREA'], ['literal', areas]], 0.25, 0],
            };
            fillLayer.paint = newFillPaint;
            this.map.setPaintProperty(mapConfig.layers.areaFAOFillLayer, 'fill-color', newFillPaint['fill-color']);
            this.map.setPaintProperty(mapConfig.layers.areaFAOFillLayer, 'fill-opacity', newFillPaint['fill-opacity']);

            if (areas.length > 1) {
                const area = this.findAreaByFAREA(this.sanitizeFAOAreaId(areas[0]), 'fao-simplified-bhxrme');

                //center map to area
                if (area) {
                    const center = turf.centroid(area.geometry).geometry.coordinates;
                    this.map.flyTo({ center });
                }
            } else {
                this.setDefaultFAOLayerProperties();
            }
        }
    }

    private sanitizeFAOAreaId(areaId: string) {
        return areaId.split('.')[0];
    }

    private findAreaByMRGID(mrgid: number, sourceLayer: string) {
        const features = this.map.querySourceFeatures(mapConfig.sources.areaIHO, {
            sourceLayer: sourceLayer,
        });
        if (!features.length) {
            return null;
        }
        return features.find((x) => x.properties.MRGID === mrgid);
    }

    private findAreaByFAREA(farea: string, sourceLayer: string) {
        const features = this.map.querySourceFeatures(mapConfig.sources.areaFAO, {
            sourceLayer: sourceLayer,
        });
        if (!features.length) {
            return null;
        }
        return features.find((x) => x.properties.F_AREA === farea);
    }

    private addButtons() {
        this.map.addControl(new MapboxControl(this.geolocationService), 'top-right');
    }

    deleteAllPolygons() {
        this.draw.deleteAll();
        this.popup.remove();
        this.drawModeActive = false;
        this.drawModeActiveTooltip = false;
    }

    projectionChange(selectedValue: string) {
        this.projection = selectedValue;
        // Set the pitch and bearing for the projection
        this.map.setProjection({ name: this.projection });
        this.map.setPitch(0);
        this.map.setBearing(0);
        if (this.globeData && !this.globeData.empty) {
            this.drawHexagons();
        } else {
            this.updatePolarHexagonLayer(this.emptyFeatureCollection());
        }
    }

    boundaryVisibilityChange(selectedValue: boolean) {
        this.showBoundaries = selectedValue;
        this.map.resetNorth();
        if (this.shapeTypeId == 1) {
            this.showBoundaries ? this.showIHOAreaLayers() : this.hideIHOAreaLayers();
        } else {
            this.showBoundaries ? this.showFAOAreaLayers() : this.hideFAOAreaLayers();
        }
    }

    resetStyle() {
        this.map.setStyle(mapConfig.styles.default);
        this.useStyle = false;
    }

    private getSummaryStatisticName(id) {
        let summaryStatistics = [
            {
                id: 'maxActivity',
                name: 'Maximum',
            },
            {
                id: 'medianActivity',
                name: 'Median',
            },
            {
                id: 'meanActivity',
                name: 'Arithmetic Mean',
            },
            {
                id: 'geometricMean',
                name: 'Geometric Mean',
            },
        ];
        for (var i = 0; i < summaryStatistics.length; i++) {
            if (summaryStatistics[i].id === id) {
                return summaryStatistics[i].name;
            }
        }

        // If no matching id is found, return null or an appropriate default value
        return null;
    }

    private setDefaultFAOLayerProperties() {
        this.map.setPaintProperty(mapConfig.layers.areaFAOLayer, 'line-color', '#3561ce');
        this.map.setPaintProperty(mapConfig.layers.areaFAOLayer, 'line-opacity', 0.7);
        this.map.setPaintProperty(mapConfig.layers.areaFAOLayer, 'line-width', 2);
        this.map.setPaintProperty(mapConfig.layers.areaFAOFillLayer, 'fill-color', ['case', ['in', 'F_AREA', ['literal', ['37']]], '#3561ce', 'transparent']);
        this.map.setPaintProperty(mapConfig.layers.areaFAOFillLayer, 'fill-opacity', 0.25);
        this.map.setPaintProperty(mapConfig.layers.areaFAOFillLayer, 'fill-outline-color', '#3561ce');
    }

    private setDefaultIHOLayerProperties() {
        this.map.setPaintProperty(mapConfig.layers.areaIHOFillLayer, 'fill-color', ['case', ['in', 'MRGID', ['literal', [1]]], '#3561ce', 'transparent']);
        this.map.setPaintProperty(mapConfig.layers.areaIHOFillLayer, 'fill-opacity', 0.15);
        this.map.setPaintProperty(mapConfig.layers.areaIHOFillLayer, 'fill-outline-color', '#3561ce');
        this.map.setPaintProperty(mapConfig.layers.areaIHOLayer, 'line-color', ['case', ['in', 'MRGID', ['literal', [1]]], 'black', '#3561ce']);
        this.map.setPaintProperty(mapConfig.layers.areaIHOLayer, 'line-opacity', 0.7);
        this.map.setPaintProperty(mapConfig.layers.areaIHOLayer, 'line-width', 2);
    }
}

class MapboxControl {
    private _map: any;
    _container: Element;

    constructor(private geolocationService: GeolocationService) {}

    onAdd(map) {
        this._map = map;
        this._container = document.getElementsByClassName('mapboxgl-ctrl mapboxgl-ctrl-group')[0];

        // Append the checkbox button to the control container
        this._container.appendChild(document.getElementById('polygon-deletion'));
        this._container.appendChild(document.getElementById('reset-default-view'));
        this._container.appendChild(document.getElementById('boundaries-checkbox'));

        // get bearing button
        const bearingEl = document.getElementsByClassName('mapboxgl-ctrl-compass')[0];
        // hide bearing button
        bearingEl.setAttribute('style', 'display: none');

        return this._container;
    }

    onRemove() {
        this._container.parentNode.removeChild(this._container);
        this._map = undefined;
    }
}
